import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loadingIndicator] - Visible automatic-loading state.
 * @property {HTMLElement} [loadAnnouncement] - Polite live-region announcement.
 * @property {HTMLElement} [endOfResults] - End-of-results message.
 * @property {HTMLElement} [loadError] - Automatic-loading error message.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Map<number, { generation: number, promise: Promise<void> }>} */
  #pageFetches = new Map();

  #cacheGeneration = 0;

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {number | null} */
  #nextPageRenderGeneration = null;

  /** @type {number | null} */
  #previousPageRenderGeneration = null;

  #filterUpdatePending = false;

  /** @type {'next' | 'previous' | null} */
  #failedLoadDirection = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#updateEndState();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.#filterUpdatePending) this.#completeFilterUpdate(this.#cacheGeneration);
  }

  /**
   * Restores pagination after a Section Rendering update. The standard event
   * promise is canonical while a filter update is pending; updatedCallback uses
   * the same idempotent path only for unrelated morphs.
   * @param {number} generation
   */
  #completeFilterUpdate(generation) {
    if (generation !== this.#cacheGeneration || !this.isConnected) return;

    this.#filterUpdatePending = false;
    this.#observeViewMore();
    this.#updateEndState();
    this.#fetchPage('next');
    this.#fetchPage('previous');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Reuse the single canonical observer while dropping stale sentinels after
    // Section Rendering, including when the new result has no sentinels.
    this.infinityScrollObserver?.disconnect();

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) {
            try {
              await viewTransition.current;
            } catch {
              // The results event owns recovery after a failed transition.
              return;
            }
          }

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) {
      return;
    }

    const cacheGeneration = this.#cacheGeneration;
    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch {
      // Speculative prefetch failure stays silent. The visible intersection
      // path retries and owns the customer-facing error state.
      if (cacheGeneration !== this.#cacheGeneration) return;
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo) || this.pages.has(pageNumber)) return;

    const cacheGeneration = this.#cacheGeneration;
    const pendingFetch = this.#pageFetches.get(pageNumber);
    if (pendingFetch?.generation === cacheGeneration) {
      await pendingFetch.promise;
      return;
    }

    const fetchPromise = sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url).then((pageContent) => {
      const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
      if (!parsedPage.querySelector('[ref="grid"]')) {
        sectionRenderer.invalidateSectionHTML(this.sectionId, pageInfo.url);
        throw new Error('The results response did not contain a product grid.');
      }

      if (cacheGeneration === this.#cacheGeneration) this.pages.set(pageNumber, pageContent);
    });
    const fetchRecord = { generation: cacheGeneration, promise: fetchPromise };

    this.#pageFetches.set(pageNumber, fetchRecord);
    try {
      await fetchPromise;
    } finally {
      if (this.#pageFetches.get(pageNumber) === fetchRecord) {
        this.#pageFetches.delete(pageNumber);
      }
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#filterUpdatePending || this.#nextPageRenderGeneration !== null) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#updateEndState();
      return;
    }

    const renderGeneration = this.#cacheGeneration;
    this.#nextPageRenderGeneration = renderGeneration;
    this.#setLoadingState(true);

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        await this.#fetchSpecificPage(nextPage.page, nextPage.url);
        if (!this.#isCurrentGrid(grid, renderGeneration)) return;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      if (!this.#isCurrentGrid(grid, renderGeneration)) return;

      const appendedCount = nextPageItemElements.length;
      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      await yieldToMainThread();

      if (!this.#isCurrentGrid(grid, renderGeneration)) return;

      history.pushState('', '', nextPage.url.toString());
      this.#failedLoadDirection = null;
      this.#announceLoadedResults(appendedCount);
      this.#updateEndState();

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } catch (error) {
      if (renderGeneration === this.#cacheGeneration) {
        this.#showLoadError('next');
        console.error('Unable to load more results.', error);
      }
    } finally {
      if (this.#nextPageRenderGeneration === renderGeneration) {
        this.#setLoadingState(false);
        this.#nextPageRenderGeneration = null;
      }
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid || this.#filterUpdatePending || this.#previousPageRenderGeneration !== null) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    const renderGeneration = this.#cacheGeneration;
    this.#previousPageRenderGeneration = renderGeneration;

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        await this.#fetchSpecificPage(previousPage.page, previousPage.url);
        if (!this.#isCurrentGrid(grid, renderGeneration)) return;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      if (!this.#isCurrentGrid(grid, renderGeneration)) return;

      // Store the current scroll position and height of the first element
      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
        const heightDiff = newHeight - oldHeight;
        scrollTo({
          top: currentScrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      await yieldToMainThread();

      if (!this.#isCurrentGrid(grid, renderGeneration)) return;

      history.pushState('', '', previousPage.url.toString());
      this.#failedLoadDirection = null;

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } catch (error) {
      if (renderGeneration === this.#cacheGeneration) {
        this.#showLoadError('previous');
        console.error('Unable to load more results.', error);
      }
    } finally {
      if (this.#previousPageRenderGeneration === renderGeneration) {
        this.#previousPageRenderGeneration = null;
      }
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    const { grid, loadingIndicator, loadError } = this.refs;
    loadingIndicator?.toggleAttribute('hidden', !isLoading);
    if (isLoading) loadError?.setAttribute('hidden', '');

    if (grid) {
      if (isLoading) grid.setAttribute('aria-busy', 'true');
      else grid.removeAttribute('aria-busy');
    }
  }

  /** @param {'next' | 'previous'} direction */
  #showLoadError(direction) {
    this.#failedLoadDirection = direction;
    this.refs.loadError?.removeAttribute('hidden');
  }

  retryPage() {
    this.refs.loadError?.setAttribute('hidden', '');
    if (this.#failedLoadDirection === 'previous') this.#renderPreviousPage();
    else this.#renderNextPage();
  }

  /**
   * @param {HTMLUListElement} grid
   * @param {number} generation
   */
  #isCurrentGrid(grid, generation) {
    return (
      generation === this.#cacheGeneration &&
      !this.#filterUpdatePending &&
      this.isConnected &&
      grid.isConnected &&
      grid === this.refs.grid
    );
  }

  /**
   * @param {number} count
   */
  #announceLoadedResults(count) {
    const { loadAnnouncement } = this.refs;
    if (!loadAnnouncement) return;

    const message = loadAnnouncement.dataset.loadedMessage || 'More products loaded';
    loadAnnouncement.textContent = '';
    requestAnimationFrame(() => {
      loadAnnouncement.textContent = `${count} ${message}`;
    });
  }

  #updateEndState() {
    const { endOfResults, viewMoreNext } = this.refs;
    if (!endOfResults) return;

    const nextPage = this.#getPage('next');
    const atEnd = !nextPage || !this.#shouldUsePage(nextPage);
    endOfResults.toggleAttribute('hidden', !atEnd);

    if (atEnd && viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
    }
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#cacheGeneration += 1;
    this.#filterUpdatePending = true;
    this.infinityScrollObserver?.disconnect();
    this.#pageFetches.clear();
    this.#nextPageRenderGeneration = null;
    this.#previousPageRenderGeneration = null;
    this.#failedLoadDirection = null;
    this.#setLoadingState(false);

    const updateGeneration = this.#cacheGeneration;
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.then(
        () => this.#completeFilterUpdate(updateGeneration),
        (error) => {
          if (error instanceof Error && 'resultsRecovered' in error && error.resultsRecovered === true) {
            this.#completeFilterUpdate(updateGeneration);
            return;
          }
          // The URL/results owner could not restore a coherent state. Keep
          // automatic append suspended until another successful update or the
          // owner's full-page recovery replaces this document.
        }
      );
    }
  };
}
